/**
 * features/students/PhotoManagement.tsx
 * ------------------------------------------------------------------
 * PHOTO MANAGEMENT — pupils' photographs, in one place.
 *
 *   PHOTOS     (admin + media) pick a class, see every current pupil's
 *              photo, download one as a .jpg or any number as a .zip.
 *   ON RECORD  (the office, and class teachers for their own class) every
 *              photo on the system at a glance, filterable by class —
 *              parents' uploads first, since those go live without anyone
 *              looking — with who put each up and when.
 *   HISTORY    (the same people) every upload, removal and refusal.
 *   REMINDERS  (super academic admin ONLY — the account Makoni holds) the
 *              standing reminder to parents of pupils with no photo: on/off,
 *              how often, the email's wording, and the "new photo needed"
 *              wording; plus what recent runs did. The sending is
 *              functions/photoReminders.js (daily, 08:00).
 *
 * Taking a photo down is for admin and the academic admins
 * (lib/studentPhotoPolicy PHOTO_REMOVER_ROLES). It offers — ticked by
 * default — to tell the parent it did not meet the standard and ask for a
 * new one (email + Parent Portal notice). Left empty, the school's standard
 * message is sent; the server fills it in, since only the super academic
 * admin can read the settings. The server checks who may remove a photo
 * again; hiding a button here is a courtesy.
 *
 * Oct 2026: replaced the separate Pupil Photos screen (features/setup/
 * PupilPhotos.tsx), whose review list is now On record + History. Gated
 * canReviewPupilPhotos; each tab then checks its own permission. Files
 * come through the storage rules every time (lib/studentPhoto) — no
 * shareable link to a child's photograph exists — and every download and
 * removal is logged.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { getBlob, ref } from 'firebase/storage';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  Camera, Download, CheckSquare, Square, Loader2, ImageOff, Trash2, BellRing, Save, X, Images,
  UserRound, Building2, AlertTriangle, CheckCircle2, History, LayoutGrid,
} from 'lucide-react';
import { storage, db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import {
  GradientHero, SoftCard, AccentButton, StatChip, PanelHeader, glassFieldClass, modalBackdrop, modalSurface, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import StudentAvatar from '@/components/ui/StudentAvatar';
import { auditFromContext } from '@/lib/audit';
import { removeStudentPhoto } from '@/lib/studentPhoto';
import {
  PHOTO_EVENTS_COLLECTION, PHOTO_ADMIN_ROLES, canRemovePhoto, visibleEvents, type PhotoEvent,
} from '@/lib/studentPhotoPolicy';
import {
  PHOTO_REMINDER_SETTINGS, PHOTO_REMINDER_LOG, FREQUENCY_CHOICES, PLACEHOLDERS,
  effectiveSettings, fillTemplate, childList, type PhotoReminderSettings,
} from '@/lib/photoReminders';
import type { Student } from '@/types';

type Tab = 'photos' | 'record' | 'history' | 'reminders';

/** A safe file name: "Surname First (ADM).jpg". */
function fileNameFor(s: Student): string {
  const base = (s.fullName || s.id).replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim();
  return `${base}${s.admissionNumber ? ` (${s.admissionNumber})` : ''}.jpg`;
}

function save(blob: Blob, name: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export default function PhotoManagement() {
  const { userRoles, permissions } = useAuth();
  const roles = userRoles ?? [];
  const canRemove = canRemovePhoto({ roles });
  const reviews = roles.some((r) => (PHOTO_ADMIN_ROLES as readonly string[]).includes(r) || r === 'classTeacher');
  const tabs = ([
    ['photos', 'Photos', Images, permissions.canDownloadPupilPhotos],
    ['record', 'On record', LayoutGrid, reviews],
    ['history', 'History', History, reviews],
    // One account only — whoever holds superAcademicAdmin (Makoni's).
    ['reminders', 'Reminders', BellRing, roles.includes('superAcademicAdmin')],
  ] as const).filter((t) => t[3]);
  const [picked, setTab] = useState<Tab | null>(null);
  const tab: Tab | undefined = tabs.find((t) => t[0] === picked)?.[0] ?? tabs[0]?.[0];

  return (
    <div className="space-y-5">
      <GradientHero icon={Camera} title="Photo management" subtitle="Pupils’ photographs — downloads, uploads and reminders" />
      {tabs.length > 1 && (
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="tablist">
          {tabs.map(([k, label, Icon]) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${tab === k
                ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
            ><Icon size={15} /> {label}</button>
          ))}
        </div>
      )}
      {tab === 'photos' && <PhotosTab canRemove={canRemove} />}
      {tab === 'record' && <UploadsTab view="record" />}
      {tab === 'history' && <UploadsTab view="history" />}
      {tab === 'reminders' && <RemindersTab />}
      {!tab && <SoftCard hover={false}><p className="text-sm text-gray-500">Nothing here is open to your account.</p></SoftCard>}
    </div>
  );
}

/* ================================================================== */

function PhotosTab({ canRemove }: { canRemove: boolean }) {
  const { userData } = useAuth();
  const { students, classes } = useData();
  const usable = useMemo(
    () => classes.filter((c) => !c.archived).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [classes],
  );
  const [classId, setClassId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<Student | null>(null);

  const cls = usable.find((c) => c.id === classId);
  const pupils = useMemo(
    () => students
      .filter((s) => s.currentClass === classId && (!s.status || s.status === 'active'))
      .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );
  const withPhoto = pupils.filter((s) => s.photo?.path);
  const chosen = withPhoto.filter((s) => picked.has(s.id));
  const allPicked = withPhoto.length > 0 && chosen.length === withPhoto.length;

  function choose(id: string) { setClassId(id); setPicked(new Set()); setError(''); setProgress(''); }
  function toggle(id: string) {
    setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  async function download(list: Student[]) {
    if (!list.length || !cls) return;
    setBusy(true);
    setError('');
    try {
      if (list.length === 1) {
        const s = list[0];
        setProgress(`Fetching ${s.fullName}…`);
        save(await getBlob(ref(storage, s.photo!.path)), fileNameFor(s));
      } else {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const folder = zip.folder(cls.name) ?? zip;
        const missed: string[] = [];
        let n = 0;
        for (const s of list) {
          n += 1;
          setProgress(`Fetching ${n} of ${list.length}…`);
          try { folder.file(fileNameFor(s), await getBlob(ref(storage, s.photo!.path))); } catch { missed.push(s.fullName); }
        }
        setProgress('Making the zip…');
        save(await zip.generateAsync({ type: 'blob' }), `${cls.name} photos.zip`);
        if (missed.length) setError(`Could not fetch ${missed.length}: ${missed.join(', ')}.`);
      }
      auditFromContext(userData, 'photos.download', 'admin', {
        targetId: cls.id, targetType: 'class', targetName: cls.name,
        details: { count: list.length, studentIds: list.map((s) => s.id) },
      });
      setProgress(`Downloaded ${list.length} photo${list.length === 1 ? '' : 's'}.`);
    } catch (e) {
      setError((e as Error).message || 'The download did not complete.');
      setProgress('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Class</span>
            <select value={classId} onChange={(e) => choose(e.target.value)} className={glassFieldClass('py-2 min-w-[14rem]')}>
              <option value="">Choose a class…</option>
              {usable.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {cls && (
            <>
              <AccentButton icon={allPicked ? Square : CheckSquare} variant="soft" disabled={busy || !withPhoto.length} onClick={() => setPicked(allPicked ? new Set() : new Set(withPhoto.map((s) => s.id)))}>
                {allPicked ? 'Clear selection' : `Select all (${withPhoto.length})`}
              </AccentButton>
              <AccentButton icon={busy ? Loader2 : Download} disabled={busy || !chosen.length} onClick={() => download(chosen)}>
                Download {chosen.length || ''} selected
              </AccentButton>
              <AccentButton icon={Download} variant="soft" disabled={busy || !withPhoto.length} onClick={() => download(withPhoto)}>
                Download whole class
              </AccentButton>
            </>
          )}
        </div>
        {cls && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            {pupils.length} pupils · {withPhoto.length} with a photo{pupils.length > withPhoto.length ? ` · ${pupils.length - withPhoto.length} without` : ''}.
            One photo downloads as a .jpg; more than one as a .zip. Every download is recorded.
          </p>
        )}
        {progress && <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">{progress}</p>}
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
      </SoftCard>

      {cls && (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {pupils.map((s) => {
            const has = !!s.photo?.path;
            const on = picked.has(s.id);
            return (
              <div
                key={s.id}
                className={`rounded-r-xl border border-gray-100 dark:border-gray-700/70 border-l-4 ${on ? 'border-l-[var(--accent-strong)] bg-violet-50 dark:bg-violet-950/30' : 'border-l-gray-200 dark:border-l-gray-600 bg-white dark:bg-gray-800'} p-3 flex flex-col items-center gap-2 text-center`}
              >
                {has ? <StudentAvatar student={s} size="lg" /> : (
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400"><ImageOff size={20} /></div>
                )}
                <p className="text-xs font-medium text-gray-800 dark:text-gray-100 leading-tight">{s.fullName}</p>
                {has ? (
                  <div className="flex items-center gap-1.5">
                    <label className="inline-flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggle(s.id)} className="accent-[var(--accent-strong)]" /> Select
                    </label>
                    <button onClick={() => download([s])} disabled={busy} title={`Download ${s.fullName}`} aria-label={`Download ${s.fullName}`}
                      className="p-1 rounded text-gray-500 hover:text-[var(--accent-strong)] hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"><Download size={14} /></button>
                    {canRemove && (
                      <button onClick={() => setRemoving(s)} disabled={busy} title={`Remove ${s.fullName}'s photo`} aria-label={`Remove ${s.fullName}'s photo`}
                        className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40"><Trash2 size={14} /></button>
                    )}
                  </div>
                ) : <span className="text-[11px] text-gray-400">No photo</span>}
              </div>
            );
          })}
          {pupils.length === 0 && <p className="text-sm text-gray-500 col-span-full">No current pupils in this class.</p>}
        </div>
      )}

      {removing && <RemovePhotoModal student={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

/* ================================================================== */

const when = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/** ON RECORD: what is up now, parents' uploads first among equals — a
 *  photo the office put up has already been looked at. HISTORY: the log. */
function UploadsTab({ view }: { view: 'record' | 'history' }) {
  const { userData, userRoles } = useAuth();
  const { classes, teachers, studentById } = useData();
  const [events, setEvents] = useState<PhotoEvent[] | null>(null);
  const [readErr, setReadErr] = useState('');
  const filter = view === 'record' ? 'live' : 'all';
  const [classId, setClassId] = useState('');
  const [removing, setRemoving] = useState<Student | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, PHOTO_EVENTS_COLLECTION), orderBy('at', 'desc'), limit(500)),
    (s) => { setReadErr(''); setEvents(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PhotoEvent)); },
    (e) => { setReadErr(e.message || 'The photo log could not be read.'); setEvents([]); },
  ), []);

  const roles = userRoles ?? [];
  const isOffice = roles.some((r) => (PHOTO_ADMIN_ROLES as readonly string[]).includes(r));
  const teacher = useMemo(() => {
    const t = teachers.find((x) => x.id === userData?.linkedTeacherId);
    return t ? { id: t.id, classTeacherOf: t.classTeacherOf } : undefined;
  }, [teachers, userData?.linkedTeacherId]);
  const classLikes = useMemo(() => classes.map((c) => ({
    id: c.id,
    classTeacherId: (c as { classTeacherId?: string }).classTeacherId,
    assistantClassTeacherId: (c as { assistantClassTeacherId?: string }).assistantClassTeacherId,
  })), [classes]);
  const className = (id?: string) => classes.find((c) => c.id === id)?.name ?? id ?? '';

  const mine = useMemo(
    () => visibleEvents(events ?? [], { roles, teacher, classes: classLikes }),
    [events, roles, teacher, classLikes],
  );

  /* One row per pupil whose photo is up now, from their latest upload —
     the log keeps every upload, but only the one on the record matters. */
  const live = useMemo(() => {
    const seen = new Set<string>();
    const rows: PhotoEvent[] = [];
    for (const e of mine) {
      if (e.action !== 'uploaded' || seen.has(e.studentId)) continue;
      seen.add(e.studentId);
      const st = studentById(e.studentId);
      if (!st?.photo) continue; // since taken down
      if (Math.abs(new Date(st.photo.updatedAt).getTime() - new Date(e.at).getTime()) > 120_000) continue; // superseded
      rows.push(e);
    }
    return rows.sort((a, b) => Number(b.source === 'parent') - Number(a.source === 'parent') || b.at.localeCompare(a.at));
  }, [mine, studentById]);

  const inClass = (cid?: string) => !classId || cid === classId;
  const shownLive = live.filter((e) => inClass(studentById(e.studentId)?.currentClass));
  const shownAll = mine.filter((e) => inClass(e.classId));
  const classOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const e of live) { const c = studentById(e.studentId)?.currentClass; if (c) ids.add(c); }
    for (const e of mine) if (e.classId) ids.add(e.classId);
    return classes.filter((c) => ids.has(c.id)).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [live, mine, classes, studentById]);

  const counts = {
    live: live.length,
    byParents: live.filter((e) => e.source === 'parent').length,
    removed: mine.filter((e) => e.action === 'removed').length,
  };

  if (events === null) return <SoftCard hover={false}><Loader2 className="animate-spin" size={16} /></SoftCard>;

  return (
    <>
      {readErr && (
        <div className="rounded-r-xl border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 px-4 py-3">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">The photo log could not be read.</p>
          <p className="text-[12px] text-gray-500 mt-1">{readErr}</p>
        </div>
      )}
      {!isOffice && !teacher && (
        <div className="rounded-r-xl border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Your account is not linked to a teacher record, so no class can be shown. Ask the office to link it.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatChip tone="navy" icon={Camera} value={counts.live} label={isOffice ? 'Photos on record' : 'Photos in your class'} />
        <StatChip tone={counts.byParents ? 'gold' : 'slate'} icon={UserRound} value={counts.byParents} label="Put up by parents" />
        <StatChip tone="slate" icon={Trash2} value={counts.removed} label="Taken down" />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Camera} tone="navy" title={filter === 'live' ? 'On record now' : 'Everything that happened'}
          subtitle="Newest first · parents’ uploads go live immediately"
          right={(
            <select value={classId} onChange={(e) => setClassId(e.target.value)} aria-label="Class"
              className={glassFieldClass('text-xs max-w-[190px]')}>
              <option value="">All classes ({filter === 'live' ? live.length : mine.length})</option>
              {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        />

        {filter === 'live' ? (
          shownLive.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">{classId ? 'No photos on record in this class.' : 'No photos on record yet.'}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {shownLive.map((e) => {
                const st = studentById(e.studentId);
                const allowed = !!st && canRemovePhoto({ roles });
                return (
                  <div key={e.id} className={`rounded-r-xl border border-gray-100 dark:border-gray-700/70 border-l-4 ${e.source === 'parent' ? 'border-l-amber-400' : 'border-l-gray-200 dark:border-l-gray-600'} bg-white dark:bg-gray-800 p-3`}>
                    <div className="flex gap-3">
                      {st && <StudentAvatar student={st} size="lg" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{e.studentName || st?.fullName}</p>
                        <p className="text-[12px] text-gray-500">{className(st?.currentClass)}</p>
                        <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                          {e.source === 'parent' ? <UserRound size={11} /> : <Building2 size={11} />}
                          {e.source === 'parent' ? 'Parent' : 'School'} · {e.byName || 'unknown'}
                        </p>
                        <p className="text-[11px] text-gray-400">{when(e.at)}</p>
                      </div>
                    </div>
                    {allowed && (
                      <button onClick={() => setRemoving(st)}
                        className="mt-2 text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-700">
                        <Trash2 size={12} className="inline mr-1" />Take down
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          shownAll.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">Nothing has happened yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                    <th className="py-2 pl-4 pr-2 font-semibold">When</th>
                    <th className="px-2 font-semibold">Pupil</th>
                    <th className="px-2 font-semibold">What</th>
                    <th className="px-2 pr-4 font-semibold">By</th>
                  </tr>
                </thead>
                <tbody>
                  {shownAll.map((e) => (
                    <tr key={e.id} className="border-b border-gray-50 dark:border-gray-800 align-top">
                      <td className="py-2 pl-4 pr-2 text-xs text-gray-500 whitespace-nowrap">{when(e.at)}</td>
                      <td className="px-2 py-2">{e.studentName || studentById(e.studentId)?.fullName || e.studentId}
                        <span className="block text-[11px] text-gray-400">{className(e.classId)}</span>
                      </td>
                      <td className="px-2 py-2 text-xs">
                        {e.action === 'uploaded' && <span className="text-emerald-700 dark:text-emerald-300"><CheckCircle2 size={12} className="inline mr-1" />Put up{e.source === 'parent' ? ' by a parent' : ''}</span>}
                        {e.action === 'removed' && <span className="text-gray-600 dark:text-gray-300"><Trash2 size={12} className="inline mr-1" />Taken down</span>}
                        {e.action === 'rejected' && <span className="text-amber-700 dark:text-amber-300"><AlertTriangle size={12} className="inline mr-1" />Refused</span>}
                        {e.reason && <span className="block text-[11px] text-gray-400">{e.reason}</span>}
                      </td>
                      <td className="px-2 pr-4 py-2 text-xs text-gray-500">{e.byName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </SoftCard>

      {removing && <RemovePhotoModal student={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

/* ================================================================== */

function RemovePhotoModal({ student, onClose }: { student: Student; onClose: () => void }) {
  // Readable by the super academic admin only. Anyone else starts from an
  // empty box, which the server turns into the school's standard message.
  const [standard, setStandard] = useState<string | null>(null);
  useEffect(() => onSnapshot(doc(db, PHOTO_REMINDER_SETTINGS, 'school'),
    (s) => setStandard(effectiveSettings(s.data() as Partial<PhotoReminderSettings> | undefined).standard ?? ''),
    () => setStandard(null)), []);
  const [reason, setReason] = useState('Does not meet the photo standard');
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const text = message ?? standard ?? '';
  const tooShort = notify && text.trim().length > 0 && text.trim().length < 10;

  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const r = await removeStudentPhoto(student.id, reason, notify ? { notifyParent: true, message: text } : undefined);
      if (!notify) setDone('Photo removed.');
      else if (r.parentNoticeError) setDone(`Photo removed, but the parent could not be told: ${r.parentNoticeError}`);
      else if (r.notified?.email) setDone(`Photo removed. ${r.notified.email} has been asked for a new one${r.notified.portalNotices ? ', with a Parent Portal notice' : ''}.`);
      else if (r.notified?.portalNotices) setDone('Photo removed. No email address on record — a Parent Portal notice was sent.');
      else setDone('Photo removed. There is no email address or parent account on record, so nobody could be told.');
    } catch (e) {
      setError((e as Error).message || 'The photo could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('w-full max-w-lg')} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="rm-title">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <StudentAvatar student={student} size="md" />
            <div>
              <h2 id="rm-title" className="text-lg font-bold text-gray-900 dark:text-white">Remove photo</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{student.fullName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-200">{done}</p>
            <div className="flex justify-end"><button className={btnPrimary} onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-3">
              <label className="block text-xs text-gray-500">Why (recorded, not sent)
                <input value={reason} onChange={(e) => setReason(e.target.value)} className={glassFieldClass('py-1.5 text-sm')} />
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-[var(--accent-strong)]" />
                Tell the parent and ask for a new photo
              </label>
              {notify && (
                <>
                  <textarea value={text} onChange={(e) => setMessage(e.target.value)} rows={10} className={glassFieldClass('text-sm font-mono')} aria-label="Message to the parent"
                    placeholder="Leave empty to send the school’s standard message, which explains what kind of photo we need. Or write your own." />
                  <p className="text-[11px] text-gray-400">
                    Sent by email and as a Parent Portal notice. {'{child}'} becomes “{student.fullName}”; {'{link}'} the Parent Portal address. The standard message is set on the Reminders tab.
                  </p>
                </>
              )}
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700">
              <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
              <button className={`${btnPrimary} !bg-red-600 hover:!bg-red-700`} onClick={confirm} disabled={busy || reason.trim().length < 3 || tooShort}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Remove{notify ? ' and tell the parent' : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */

interface RunLog { id: string; ranAt: string; pupilsWithoutPhoto: number; addresses: number; sent: number; notDue: number; failed: number; noAddress: number }

function RemindersTab() {
  const { userData } = useAuth();
  const { students, classes } = useData();
  const [saved, setSaved] = useState<PhotoReminderSettings | null>(null);
  const [draft, setDraft] = useState<PhotoReminderSettings | null>(null);
  const [runs, setRuns] = useState<RunLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => onSnapshot(doc(db, PHOTO_REMINDER_SETTINGS, 'school'), (s) => {
    const v = effectiveSettings(s.data() as Partial<PhotoReminderSettings> | undefined);
    setSaved(v);
    setDraft((d) => d ?? v);
  }, () => { const v = effectiveSettings(null); setSaved(v); setDraft((d) => d ?? v); }), []);
  useEffect(() => onSnapshot(query(collection(db, PHOTO_REMINDER_LOG), orderBy('ranAt', 'desc'), limit(5)),
    (s) => setRuns(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<RunLog, 'id'>) }))), () => setRuns([])), []);

  const archived = useMemo(() => new Set(classes.filter((c) => c.archived).map((c) => c.id)), [classes]);
  const without = students.filter((s) => s.currentClass && !archived.has(s.currentClass) && (!s.status || s.status === 'active') && !s.photo?.path);

  if (!draft || !saved) return <SoftCard hover={false}><Loader2 className="animate-spin" size={16} /></SoftCard>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (p: Partial<PhotoReminderSettings>) => setDraft({ ...draft, ...p });
  const sample = { parent: 'Mrs Tau', children: childList(['Tau Asa Ayla', 'Tau Neo']), school: 'Livingstone Kolobeng College', link: 'https://lkcportal.com/?portal=parent' };

  async function saveSettings() {
    setBusy(true);
    setMsg('');
    try {
      const v = { ...draft!, updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? 'unknown' };
      await setDoc(doc(db, PHOTO_REMINDER_SETTINGS, 'school'), v as unknown as Record<string, unknown>);
      auditFromContext(userData, 'photos.reminders.settings', 'admin', { details: { enabled: v.enabled, frequencyDays: v.frequencyDays } });
      setMsg('Saved.');
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><BellRing size={16} /> Reminder to parents with no photo</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Checked every morning at 08:00. Each family is emailed no more often than you choose, naming every child of theirs still without a photo, until a photo arrives.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-200 mt-2"><b className="tabular-nums">{without.length}</b> current pupils have no photo right now.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="w-5 h-5 accent-[var(--accent-strong)]" />
            {draft.enabled ? 'On' : 'Off'}
          </label>
        </div>
        <div className="grid gap-3 mt-4 sm:grid-cols-[12rem_1fr]">
          <label className="text-xs text-gray-500">How often
            <select value={draft.frequencyDays} onChange={(e) => set({ frequencyDays: Number(e.target.value) })} className={glassFieldClass('py-1.5 text-sm')}>
              {FREQUENCY_CHOICES.map((f) => <option key={f.days} value={f.days}>{f.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500">Subject
            <input value={draft.subject} onChange={(e) => set({ subject: e.target.value })} className={glassFieldClass('py-1.5 text-sm')} />
          </label>
          <label className="text-xs text-gray-500 sm:col-span-2">Email
            <textarea value={draft.body} onChange={(e) => set({ body: e.target.value })} rows={11} className={glassFieldClass('text-sm font-mono')} />
          </label>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          {PLACEHOLDERS.map((p) => `${p.token} — ${p.means}`).join(' · ')}
        </p>
        <details className="mt-3">
          <summary className="text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer">Preview</summary>
          <div className="mt-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3 text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-100">
            <b>{fillTemplate(draft.subject, sample)}</b>{'\n\n'}{fillTemplate(draft.body, sample)}
          </div>
        </details>
      </SoftCard>

      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-900 dark:text-white">When a photo is removed</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">The message offered to the parent when an admin removes a photo that does not meet the standard. It can still be changed each time.</p>
        <textarea value={draft.standard ?? ''} onChange={(e) => set({ standard: e.target.value })} rows={10} className={glassFieldClass('text-sm font-mono mt-3')} aria-label="Photo standard message" />
        <p className="text-[11px] text-gray-400 mt-1">{'{child}'} — the pupil’s name · {'{link}'} — the Parent Portal address · {'{school}'} — the school’s name</p>
      </SoftCard>

      <div className="flex items-center gap-3">
        <button className={btnPrimary} disabled={busy || !dirty} onClick={saveSettings}><Save size={15} /> Save</button>
        {dirty && <button className={btnGhost} disabled={busy} onClick={() => setDraft(saved)}>Discard changes</button>}
        {msg && <span className="text-sm text-gray-600 dark:text-gray-300">{msg}</span>}
      </div>

      <SoftCard pad="p-0" hover={false}>
        <h3 className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-semibold text-sm text-gray-900 dark:text-white">Recent runs</h3>
        {runs.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">No reminders sent yet{saved.enabled ? ' — the first run is at 08:00.' : ' — reminders are off.'}</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 px-4">Day</th><th className="py-2 px-4 text-right">No photo</th><th className="py-2 px-4 text-right">Emailed</th><th className="py-2 px-4 text-right">Not due yet</th><th className="py-2 px-4 text-right">No address</th><th className="py-2 px-4 text-right">Failed</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {runs.map((r) => (
                <tr key={r.id} className="tabular-nums">
                  <td className="py-1.5 px-4">{r.id}</td><td className="py-1.5 px-4 text-right">{r.pupilsWithoutPhoto}</td><td className="py-1.5 px-4 text-right">{r.sent}</td>
                  <td className="py-1.5 px-4 text-right">{r.notDue}</td><td className="py-1.5 px-4 text-right">{r.noAddress}</td><td className="py-1.5 px-4 text-right">{r.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SoftCard>
    </div>
  );
}
